import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Button,
  Flashbar,
  Header,
  Pagination,
  PropertyFilter,
  SpaceBetween,
  Table,
} from '@amzn/awsui-components-react';
import { get } from 'lodash';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PROPERTY_FILTERING_I18N_CONSTANTS } from '../../../utils/constants';
import { ExportCSV } from '../../../utils/exportCSV';
import {
  EmptyState,
  getFilterCounterText,
  Preferences,
} from '../../../utils/tablePreferences';
import { InfoLink } from '../Common/InfoLink';
import {
  DEFAULT_PREFERENCES,
  FILTERING_PROPERTIES,
  otsVerifyAllTableColumns,
  PAGE_SIZE_OPTIONS,
  VISIBLE_CONTENT_OPTIONS,
} from './columns';
import { useOtsVerifyAllQuery } from './verifyAllQuery';

interface VerifyProps {
  updateTools: () => void;
  // eslint-disable-next-line
  updatePanel: ([]) => void;
}

const All = (props: VerifyProps) => {
  const { loading, data } = useOtsVerifyAllQuery();
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const otsAll = get(data, 'otsVerifyAll.otsVerifyItems', []);
  const [message, setMessage] = useState([]);
  const location = useLocation();

  useEffect(() => {
    if (location.state) {
      setMessage([
        {
          type: 'success',
          content: location.state['detail'],
          dismissible: true,
          dismissLabel: 'Dismiss message',
          onDismiss: () => setMessage([]),
        },
      ]);
    }
  }, [location]);

  useEffect(() => {
    props.updatePanel([
      `Table is refreshed every 30 minutes. During the refresh, the content will be cleared. \
      So when you see empty or less data, please refresh the page in 5 minutes. \
      The refresh process itself takes about 5-10 minutes. \
      Use “Last Update Time” to determine when the refresh process started.`,
    ]);
    // eslint-disable-next-line
  }, []);

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    propertyFilterProps,
    paginationProps,
  } = useCollection(otsAll, {
    propertyFiltering: {
      filteringProperties: FILTERING_PROPERTIES,
      empty: (
        <EmptyState
          title="No Accounts"
          subtitle="No Accounts to display."
          action=""
        />
      ),
      noMatch: (
        <EmptyState
          title="No matches"
          subtitle="We can’t find a match."
          action={
            <Button onClick={() => actions.setFiltering('')}>
              Clear filter
            </Button>
          }
        />
      ),
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
  });
  return (
    <SpaceBetween direction="vertical" size="l">
      <Flashbar items={message} />

      <Table
        {...collectionProps}
        header={
          <div>
            <Header
              counter={`(${items.length})`}
              info={
                props.updateTools && <InfoLink onFollow={props.updateTools} />
              }
              actions={
                <SpaceBetween direction="horizontal" size="s">
                  <ExportCSV
                    data={items}
                    filename={'OTS_verify_all.csv'}
                    loading={loading}
                  />
                </SpaceBetween>
              }
            >
              OTS Verify All
            </Header>
            <p>
              Domain-level view of periodic compliance check for each domain
              belonging to an onboarded account.
            </p>
          </div>
        }
        columnDefinitions={otsVerifyAllTableColumns}
        stickyHeader={true}
        resizableColumns={false}
        wrapLines={preferences.wraplines}
        loading={loading}
        loadingText={loading ? 'Loading OTS Data' : 'No OTS domains found'}
        visibleColumns={preferences.visibleContent}
        items={items}
        pagination={
          <Pagination
            {...paginationProps}
            ariaLabels={{
              nextPageLabel: 'Next page',
              previousPageLabel: 'Previous page',
              pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
            }}
          />
        }
        filter={
          <PropertyFilter
            className="input-filter"
            i18nStrings={PROPERTY_FILTERING_I18N_CONSTANTS}
            {...propertyFilterProps}
            countText={getFilterCounterText(filteredItemsCount)}
          />
        }
        preferences={
          <Preferences
            preferences={preferences}
            setPreferences={setPreferences}
            disabled={false}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            visibleContentOptions={VISIBLE_CONTENT_OPTIONS}
          />
        }
      />
    </SpaceBetween>
  );
};

export { All };
